import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import KwaKeUpMap from './components/KwaKeUpMap'
import { Dialog, DialogContent, Grid, Icon, Typography } from '@mui/material'
import ApplicationConf from 'conf/ApplicationConf'
import TabList from 'common/components/tab/TabList'
import useTitles from 'common/hooks/useTitles'
import useFetchTimeout from 'common/hooks/useFetchTimeout'
import UptimeTab from 'dashboard/components/UptimeTab'
import moment from 'moment'
import KwakeUpTable from './components/KwakeUpTable'
import TestHeadersDialog from './components/TestHeadersDialog'
import DashboardTimeErrors from './components/DashboardTimeErrors'
import UnupdatedTable from './components/UnupdatedTable'
import useActions from 'common/hooks/useActions'
import { DATA_BANK_STATUS, OTHER, REFRESH, SOUND } from 'common/components/action/ActionConstant'
import { countBy, differenceWith, flatten, groupBy, isNil, keys, last, toLower, uniqBy } from 'lodash'
import soundCaBugNormal from 'assets/sound/caBugNormal.mp3'
import soundCKC from 'assets/sound/ckc.mp3'
import soundCToutRouge from 'assets/sound/cToutRouge.mp4'
import soundGVu from 'assets/sound/gVu.mp4'
import soundCTropCalme from 'assets/sound/cTropCalme.mp4'
import soundCOk from 'assets/sound/cOk.mp4'
import soundHouston from 'assets/sound/houstononaunpbm.mp4'
import soundSerge from 'assets/sound/serge.mp3'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useSound from 'common/hooks/useSound'
import { BRANCH, DISPLAY_MODE, getBranchColor, STATE, VIGILANCE } from 'dashboard/utils/DashboardUtils'
import Chip from 'common/components/Chip'
import { INSTANCE_TYPE } from 'supervision/instance/constants/InstanceConstants'
import Input from 'common/components/form/Input'
import { STATUS } from 'common/constants/storeConstants'
import { fetchCustomers } from 'gdl/customers/CustomersSlice'
import Select from 'common/components/form/Select'
import { Circle } from '@mui/icons-material'
import useLocalStorage from 'common/hooks/useLocalStorage'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import Checkbox from 'common/components/form/Checkbox'
import { DEFAULT_SETTINGS_VIEW } from 'common/constants/Constants'
import { toast } from 'react-toastify'
import { useFullscreen } from 'common/hooks/useFullScreen'
import { getFullDate, shortenHumanize } from 'common/utils/DateUtil'
import { PASTEL_COLOR } from 'common/utils/ColorUtil'
import { useNavigate, useParams } from 'react-router'

const MAP = 'map'
const TABLE = 'table'
const UNUPDATED_TABLE = 'unupdated-table'
const TIME_ERROR = 'time-error'
const UPTIME = 'uptime'
const JOBS = 'jobs'

const Notification = ({
    results = [],
    showToastrs = true,
}) => {
    const [playCToutRouge] = useSound(soundCToutRouge)
    const [playCKC] = useSound(soundCKC)
    const [playCaBugNormal] = useSound(soundCaBugNormal)
    const [playGVu] = useSound(soundGVu)
    const [playCTropCalme] = useSound(soundCTropCalme)
    const [playCOk] = useSound(soundCOk)
    const [playHouston] = useSound(soundHouston)
    const [playSerge] = useSound(soundSerge)

    const {
        isMute,
    } = useSelector(store => ({
        isMute: store.app.isMute,
    }), shallowEqual)
    const [prevError, setPrevError] = useState([])
    const [dateLastError, setDateLastError] = useState(moment())

    useEffect(() => {
        const errors = results.filter(r => !r.isRunning)
        const newErrors = differenceWith(errors, prevError, (e, p) => e.instance.id === p.instance.id && e.service.id === p.service.id)
        setPrevError(errors)

        if (!errors.length && prevError.length) {
            if (isMute) return
            if (Math.random() < 0.01) playSerge()
            else playCOk()
        }

        if (!newErrors.length) {
            const now = moment()
            const time = moment.duration(now.diff(dateLastError)).valueOf()
            if (time > 3600000) { // 1h
                setDateLastError(now)
                if (!isMute) playCTropCalme()
            }
        }

        if (newErrors.length) {
            setDateLastError(moment().valueOf())
            const uniqServer = uniqBy(newErrors, 'instance.server').map(e => e.instance.server)
            const serverErrors = uniqServer.filter(server => {
                const allServerError = errors.filter(e => e.instance.server === server)
                const allService = results.filter(r => r.instance.server === server)
                return allServerError.length === allService.length
            })
            if (serverErrors.length) { // crash server
                if (showToastrs) toast.error(`Erreur serveur : ${serverErrors.join(', ')}`, { autoClose: false, hideProgressBar: true, closeOnClick: true })
                if (!isMute) playCToutRouge()
            }
            setTimeout(() => {
                const uniqInstance = uniqBy(newErrors, 'instance.id').filter(e => !serverErrors.includes(e.instance.server)).map(e => e.instance.id)
                const instanceErrors = uniqInstance.filter(instance => {
                    const allInstanceError = errors.filter(e => e.instance.id === instance)
                    const allService = results.filter(r => r.instance.id === instance)
                    return allInstanceError.length === allService.length
                })
                if (instanceErrors.length) { // crash instance
                    if (showToastrs) {
                        toast.error(
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                {`Erreur instance :\n${instanceErrors.map(id => newErrors.find(e => e.instance.id === id)?.instance.name).join('\n')}`}
                            </div>,
                            {
                                autoClose: false,
                                hideProgressBar: true,
                                closeOnClick: true,
                            },
                        )
                    }
                    if (!isMute) playCKC()
                }
                setTimeout(() => {
                    // todo add branch info on error
                    const othersErrors = newErrors.filter(e => !serverErrors.includes(e.instance.server) && !instanceErrors.includes(e.instance.id))
                    if (othersErrors.length) {
                        if (showToastrs) {
                            toast.error(
                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                    {`Erreurs services :\n${othersErrors.map(e => `[${e.instance.server}] ${e.instance.name} : ${e.service.name}`).join('\n')}`}
                                </div>,
                                {
                                    autoClose: false,
                                    hideProgressBar: true,
                                    closeOnClick: true,
                                },
                            )
                        }
                        if (isMute) return
                        const rand = Math.random()
                        if (rand < 0.80) playHouston()
                        else if (rand < 0.95) playCaBugNormal()
                        else playGVu()
                    }
                }, instanceErrors.length ? 2000 : 0)
            }, serverErrors.length ? 2000 : 0)
        }
    }, [results])

    return
    // return (
    //     <Grid container spacing={1} style={{ paddingBottom: '20px' }}>
    //         <Grid size='auto'><Button variant='contained' color='primary' onClick={playCToutRouge}>CToutRouge</Button></Grid>
    //         <Grid size='auto'><Button variant='contained' color='primary' onClick={playCKC}>CKC</Button></Grid>
    //         <Grid size='auto'><Button variant='contained' color='primary' onClick={playCaBugNormal}>CaBugNormal</Button></Grid>
    //         <Grid size='auto'><Button variant='contained' color='primary' onClick={playGVu}>gVu</Button></Grid>
    //         <Grid size='auto'><Button variant='contained' color='primary' onClick={playCTropCalme}>cTropCalme</Button></Grid>
    //         <Grid size='auto'><Button variant='contained' color='primary' onClick={playCOk}>cOk</Button></Grid>
    //         <Grid size='auto'><Button variant='contained' color='primary' onClick={playSerge}>serge</Button></Grid>
    //         <Grid size='auto'><Button variant='contained' color='primary' onClick={playHouston}>houston</Button></Grid>
    //     </Grid>
    // )
}

Notification.propTypes = {
    results: PropTypes.arrayOf(PropTypes.object),
}

const SettingsViewDialog = ({
    open,
    settings = {},
    onChange = () => {},
    onClose = () => {},
}) => {
    const [settingsTmp, setSettingsTmp] = useState(settings)

    return (
        <Dialog open={open}>
            <BasicDialogTitle onClose={onClose}>
                Personnaliser la vue
            </BasicDialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Checkbox
                        label='Afficher les serveurs sans instance (serveur interne, mantis, cloud, etc)'
                        checked={settingsTmp.showServsWithoutInstances}
                        onToggle={() => setSettingsTmp((prev) => ({ ...prev, showServsWithoutInstances: !prev.showServsWithoutInstances })) }
                    />
                    <Checkbox
                        label='Afficher les erreurs de sauvegarde (serveur rouge)'
                        checked={settingsTmp.showSaveError}
                        onToggle={() => setSettingsTmp((prev) => ({ ...prev, showSaveError: !prev.showSaveError })) }
                    />
                    <Checkbox
                        label={'Afficher les alertes d\'espaces disques (serveur rayé orange ou rouge)'}
                        checked={settingsTmp.showDiskSpaceWarning}
                        onToggle={() => setSettingsTmp((prev) => ({ ...prev, showDiskSpaceWarning: !prev.showDiskSpaceWarning })) }
                    />
                    <Checkbox
                        label={'Afficher les alertes d\'absence de supervision (serveur orange)'}
                        checked={settingsTmp.showSupervisionWarning}
                        onToggle={() => setSettingsTmp((prev) => ({ ...prev, showSupervisionWarning: !prev.showSupervisionWarning })) }
                    />
                    <Checkbox
                        label={'Masquer les toastrs d\'erreurs'}
                        checked={settingsTmp.hideToastrs}
                        onToggle={() => setSettingsTmp((prev) => ({ ...prev, hideToastrs: !prev.hideToastrs })) }
                    />
                    <Grid container size={12} alignItems='center' columnSpacing={1}>
                        <Grid size={6}>
                            <Input
                                label={'Nombre de colonnes'}
                                type='number'
                                value={settingsTmp.nbColumns ?? DEFAULT_SETTINGS_VIEW.nbColumns}
                                onChange={(v) => setSettingsTmp((prev) => ({ ...prev, nbColumns: v })) }
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                label={'Nombre d\'éléments par colonne'}
                                type='number'
                                value={settingsTmp.mapColumnsSize ?? DEFAULT_SETTINGS_VIEW.mapColumnsSize}
                                onChange={(v) => setSettingsTmp((prev) => ({ ...prev, mapColumnsSize: v })) }
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <BasicDialogAction
                buttonLabel='Valider'
                onValidate={() => {
                    onChange(settingsTmp)
                    onClose()
                }}
            />
        </Dialog>
    )
}

SettingsViewDialog.propTypes = {
    open: PropTypes.bool,
    settings: PropTypes.shape({}),
    onChange: PropTypes.func,
    onClose: PropTypes.func,
}

const DashboardApp = () => {
    const { tab = MAP } = useParams()
    const [openSettingsView, setOpenSettingsView] = useState(false)
    const [openTestHeaders, setOpenTestHeaders] = useState(false)
    const [filter, setFilter] = useLocalStorage('dashboard:filter', {})

    const [displayMode, setDisplayMode] = useLocalStorage('dashboard:displayMode', DISPLAY_MODE.BACK)
    const [vigilance, setVigilance] = useLocalStorage('dashboard:vigilance')
    // Le mode "Jobs" a son propre onglet : une ancienne valeur JOB stockée retombe sur Back-end.
    const mapDisplayMode = displayMode === DISPLAY_MODE.JOB ? DISPLAY_MODE.BACK : displayMode

    const [search, setSearch] = useLocalStorage('dashboard:search', '')
    const [branch, setBranch] = useLocalStorage('dashboard:branch')
    const [instanceType, setInstanceType] = useLocalStorage('dashboard:instanceType')

    const navigate = useNavigate()

    const [settingsView, setSettingsView] = useLocalStorage('settingsViewMap', DEFAULT_SETTINGS_VIEW)

    const {
        data: { instances = [], services = [], internalPingStartDate, internalPingEndDate } = {},
        status,
        resultDate,
        reset,
        clear,
        reload,
    } = useFetchTimeout(ApplicationConf.dashboard(), undefined, 15000, { skipLoadingState: true })

    const { data: uptimeData, reload: reloadUptimeData } = useFetchTimeout(ApplicationConf.uptime.heartbeat(), undefined, 5000, { skipLoadingState: true })

    const containerRef = useRef(null)
    const { toggle } = useFullscreen(containerRef)
    const uptimeStatusDown = useMemo(() => {
        const dataKeys = keys(uptimeData?.heartbeatList)
        return dataKeys.some(k => last(uptimeData?.heartbeatList[k])?.status !== 1)
    }, [uptimeData])

    useActions(() => [{
        type: OTHER,
        icon: 'circle',
        tooltip: `Status uptime : ${uptimeStatusDown ? 'KO' : 'OK'}`,
        color: uptimeStatusDown ? PASTEL_COLOR.red : PASTEL_COLOR.green,
        onClick: () => navigate(`/dashboard/${UPTIME}`),
    }, {
        type: REFRESH,
        onClick: () => {
            reload()
            reloadUptimeData()
        },
        loading: status === STATUS.LOADING,
    }, {
        type: OTHER,
        icon: 'tune',
        tooltip: 'Personnaliser la vue',
        onClick: () => setOpenSettingsView(true),
    }, {
        type: OTHER,
        icon: 'fullscreen',
        tooltip: 'Plein écran',
        onClick: () => toggle(),
    }, {
        type: OTHER,
        icon: 'vpn_key',
        tooltip: 'Tester un appel de supervision',
        onClick: () => setOpenTestHeaders(true),
    }, {
        type: SOUND,
    }, {
        type: DATA_BANK_STATUS,
    }], [status, uptimeStatusDown])

    useTitles(() => [{
        label: 'Dashboard',
        href: '/',
    }, {
        label: `${getFullDate(internalPingStartDate)} -> ${getFullDate(internalPingEndDate)} (${shortenHumanize(moment.duration(moment(internalPingEndDate).diff(moment(internalPingStartDate))).valueOf())})`,
        href: '/',
    }], [internalPingStartDate, internalPingEndDate])

    const dispatch = useDispatch()
    useEffect(() => {
        dispatch(fetchCustomers())
    }, [dispatch])

    const {
        customers,
    } = useSelector(store => ({
        customers: store.gdl.customers.all,
    }), shallowEqual)

    const filteredInstances = useMemo(() => {
        const formattedSearch = toLower(search)
        const filterBySearch = formattedSearch ? instances.filter(i => toLower(i.name).includes(formattedSearch)) : instances
        const filterByBranch = instanceType ? filterBySearch.filter(i => (i.instanceType ?? 'Inconnue') === instanceType) : filterBySearch
        const results = branch ? filterByBranch.filter(i => (i.branch ?? BRANCH.UNKNOWN) === branch) : filterByBranch
        return results.map(i => {
            const customer = customers.find(c => c.instances?.some(ci => ci.instance === i.instanceId))
            return {
                ...i,
                customer,
            }
        }).filter(i => {
            if (!vigilance) return true
            return i.customer?.vigilance === vigilance
        })
    }, [branch, instanceType, instances, search, customers, vigilance])

    const nbVigilanceHigh = useMemo(() => filteredInstances.filter(i => i.customer?.vigilance === VIGILANCE.HIGH).length, [filteredInstances])
    const nbVigilanceMedium = useMemo(() => filteredInstances.filter(i => i.customer?.vigilance === VIGILANCE.MEDIUM).length, [filteredInstances])

    useEffect(() => {
        if (tab === TIME_ERROR) {
            clear()
            return
        }
        reset()
    }, [tab])

    // Base des compteurs de pastilles : recherche + vigilance appliquées (mais PAS les filtres
    // type/branche, qui se croisent entre eux ci-dessous).
    const instancesForCounts = useMemo(() => {
        const formattedSearch = toLower(search)
        const bySearch = formattedSearch ? instances.filter(i => toLower(i.name).includes(formattedSearch)) : instances
        return bySearch
            .map(i => ({ ...i, customer: customers.find(c => c.instances?.some(ci => ci.instance === i.instanceId)) }))
            .filter(i => !vigilance || i.customer?.vigilance === vigilance)
    }, [instances, search, customers, vigilance])

    // Compteurs croisés : les types comptent dans la branche sélectionnée (total = nb d'instances
    // de cette branche), et les branches comptent dans le type sélectionné.
    const groundByInstanceTypes = useMemo(() => {
        const base = branch ? instancesForCounts.filter(i => (i.branch ?? BRANCH.UNKNOWN) === branch) : instancesForCounts
        return groupBy(base, i => i.instanceType?.startsWith('Aqua6BO') ? 'Aqua6BO' : (i.instanceType ?? 'Inconnue'))
    }, [instancesForCounts, branch])
    const groundByBranch = useMemo(() => {
        const base = instanceType ? instancesForCounts.filter(i => (i.instanceType ?? 'Inconnue') === instanceType) : instancesForCounts
        return groupBy(base, i => i.branch ?? BRANCH.UNKNOWN)
    }, [instancesForCounts, instanceType])

    return (
        <div>
            <Notification results={services} showToastrs={!settingsView.hideToastrs} />
            <TabList
                ref={containerRef}
                controlTab={tab}
                setControlTab={v => navigate(`/dashboard/${v}`)}
                tabs={[
                    {
                        constant: MAP,
                        label: 'Cartographie',
                        icon: 'map',
                    },
                    {
                        constant: JOBS,
                        label: 'Jobs',
                        icon: 'autorenew',
                    },
                    {
                        constant: TABLE,
                        label: 'Tableau',
                        icon: 'table_view',
                    },
                    {
                        constant: UNUPDATED_TABLE,
                        label: 'Tableau services non mis à jour',
                        icon: 'playlist_remove',
                    },
                    {
                        constant: TIME_ERROR,
                        label: 'Time error',
                        icon: 'running_with_errors',
                    },
                    {
                        constant: UPTIME,
                        label: 'Uptime',
                        icon: 'monitor_heart',
                    },
                ]}
                headerComponent={resultDate && (
                    <Grid container columnSpacing={1} sx={{ margin: '0 10 5 10' }}>
                        {status === STATUS.REJECTED && (
                            <Grid container size='auto' direction='column'>
                                <Grid container spacing={0.5}>
                                    <Icon sx={{ color: 'red' }}>warning</Icon>
                                    Impossible de récupérer l'état des serveurs
                                </Grid>
                            </Grid>
                        )}
                        <Grid container size='grow' columnSpacing={1} rowSpacing={0.2}>
                            <Grid container size={12}>
                                {keys(groundByInstanceTypes).map(key => {
                                    const nb = groundByInstanceTypes[key].length
                                    const {
                                        error = 0,
                                        warning = 0,
                                        clean = 0,
                                    } = countBy(groundByInstanceTypes[key], i => {
                                        if (i.statusBackend === STATE.KO || i.statusDB === STATE.KO) return 'error'
                                        if (i.statusBackend === STATE.OK && i.statusDB === STATE.OK) return 'clean'
                                        return 'warning'
                                    })
                                    const type = INSTANCE_TYPE.find(t => t.value === key)
                                    return (
                                        <Grid key={key} size='auto'>
                                            <Chip
                                                label={`${nb} ${type?.label ?? 'Inconnue'}`}
                                                color={isNil(instanceType) || instanceType === key ? type?.color : '#aaa'}
                                                tooltip={`${error} en erreur, ${warning} à surveiller, ${clean} sans problème)`}
                                                onClick={() => setInstanceType(prev => prev === key ? undefined : key)}
                                            />
                                        </Grid>
                                    )
                                })}
                            </Grid>
                            <Grid container size={12}>
                                <Grid size='auto'>
                                    <Chip
                                        label={`${(uniqBy(instances, 'server').length ?? 0)} Serveur`}
                                        color='#a22ade'
                                    />
                                </Grid>
                                {keys(groundByBranch).map(key => {
                                    const nb = groundByBranch[key].length
                                    const {
                                        error = 0,
                                        warning = 0,
                                        clean = 0,
                                    } = countBy(groundByBranch[key], i => {
                                        if (i.statusBackend === STATE.KO || i.statusDB === STATE.KO) return 'error'
                                        if (i.statusBackend === STATE.OK && i.statusDB === STATE.OK) return 'clean'
                                        return 'warning'
                                    })
                                    return (
                                        <Grid key={key} size='auto'>
                                            <Chip
                                                label={`${nb} ${key}`}
                                                color={isNil(branch) || branch === key ? getBranchColor(key) : '#aaa'}
                                                tooltip={`${error} en erreur, ${warning} à surveiller, ${clean} sans problème)`}
                                                onClick={() => setBranch(prev => prev === key ? undefined : key)}
                                            />
                                        </Grid>)
                                })}
                            </Grid>
                        </Grid>
                        <Grid size={1}>
                            <Input
                                label='Rechercher'
                                value={search}
                                onChange={setSearch}
                            />
                        </Grid>
                        {tab !== JOBS && (
                            <Grid size={1.5}>
                                <Select
                                    label={'Mode d\'affichage'}
                                    items={[
                                        { value: DISPLAY_MODE.BACK, label: 'Back-end', icon: 'thunderstorm' },
                                        { value: DISPLAY_MODE.DB, label: 'Base de donnée', icon: 'storage' },
                                        { value: DISPLAY_MODE.FRONT, label: 'Front-end', icon: 'desktop_access_disabled' },
                                    ]}
                                    value={mapDisplayMode}
                                    onChange={setDisplayMode}
                                />
                            </Grid>
                        )}
                        <Grid size={1}>
                            <Select
                                label={'Vigilance'}
                                items={[
                                    {
                                        label: `Forte (${nbVigilanceHigh})`,
                                        value: VIGILANCE.HIGH,
                                        icon: <Circle sx={{ color: 'red', fontSize: '1rem' }} />,
                                    },
                                    {
                                        label: `Moyenne (${nbVigilanceMedium})`,
                                        value: VIGILANCE.MEDIUM,
                                        icon: <Circle sx={{ color: 'orange', fontSize: '1rem' }} />,
                                    },
                                ]}
                                value={vigilance}
                                onChange={setVigilance}
                                undefinedValue
                            />
                        </Grid>
                    </Grid>
                )}
            >
                {t => {
                    if (status === STATUS.IDLE) {
                        return (
                            <div>
                                Chargement en cours...
                            </div>
                        )
                    }
                    if (!services.length && !instances.length) {
                        return (
                            <div>
                                Aucune donnée à afficher
                            </div>
                        )
                    }
                    if (t === TABLE) {
                        return (
                            <KwakeUpTable
                                results={services}
                                filter={filter}
                                setFilter={setFilter}
                            />
                        )
                    }
                    if (t === TIME_ERROR) {
                        return <DashboardTimeErrors />
                    }
                    if (t === UPTIME) {
                        return <UptimeTab uptimeData={uptimeData} />
                    }
                    if (t === UNUPDATED_TABLE) {
                        return (
                            <UnupdatedTable
                                results={[
                                    ...services,
                                    ...flatten(instances.map(i => i.frontendVersions.map((v) => ({
                                        ...v,
                                        branch: i.branch,
                                        service: { id: v.application },
                                        instance: i,
                                    })))),
                                ]}
                            />
                        )
                    }
                    return (
                        <>
                            {filteredInstances.length === 0 && (
                                <Typography variant='h5' gutterBottom>Aucune instances à afficher</Typography>
                            )}
                            <KwaKeUpMap
                                instances={filteredInstances}
                                onClick={info => {
                                    setFilter(info)
                                    navigate(`/dashboard/${TABLE}`)
                                }}
                                displayMode={t === JOBS ? DISPLAY_MODE.JOB : mapDisplayMode}
                                services={services}
                                settingsView={settingsView}
                            />
                        </>
                    )
                }}
            </TabList>
            <SettingsViewDialog
                open={openSettingsView}
                settings={settingsView}
                onChange={setSettingsView}
                onClose={() => setOpenSettingsView(false)}
            />
            <TestHeadersDialog
                open={openTestHeaders}
                onClose={() => setOpenTestHeaders(false)}
            />
        </div>
    )
}

export default DashboardApp
